import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { ArrowPathIcon } from '../../components/Icons';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface TenantRow {
  id: string;
  name: string;
  plan: string;
  website_domain: string;
  total_leads: number;
  revenue_30d: string;
  leads_30d: number;
  status: string;
  created_at: string | null;
}

interface TenantDetail {
  id: string;
  name: string;
  domain: string;
  is_active: boolean;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  subscription: {
    id?: string;
    status: string;
    plan: string;
    tier: string;
    plan_code: string | null;
    billing_period: string | null;
    price: string;
    is_active: boolean;
    trial_ends_at: string | null;
    current_period_end: string | null;
  } | null;
  access_controls?: {
    billing_lock_mode: 'inherit' | 'force_locked' | 'force_unlocked';
    billing_locked_by_subscription: boolean;
    billing_locked_effective: boolean;
    feature_overrides: Record<string, unknown>;
    updated_at: string | null;
    updated_by: string | null;
  };
  entitlements: {
    tier: string;
    tier_display: string;
    plan_code: string | null;
    includes_portalr: boolean;
    max_users: number;
    custom_pipeline: boolean;
    email_enabled: boolean;
    email_monthly_limit: number;
    automation_enabled: boolean;
    daily_lead_limit?: number;
    max_users_ceiling?: number | null;
    max_contacts?: number | null;
    max_websites?: number | null;
    enrichment_enabled?: boolean;
    prospecting_enabled?: boolean;
    api_access_enabled?: boolean;
    feature_flags?: Record<string, boolean>;
    override_source?: {
      has_override: boolean;
      applied_fields: string[];
    };
  };
  metrics: {
    total_users: number;
    active_users: number;
    total_websites: number;
    active_websites: number;
    total_leads: number;
    leads_30d: number;
    revenue_30d: string;
    latest_event_at: string | null;
  };
  websites: Array<{
    id: string;
    domain: string;
    is_active: boolean;
    deleted_at: string | null;
    last_event_at: string | null;
    created_at: string | null;
  }>;
}

interface SubscriptionFormState {
  status: string;
  tier: string;
  trial_ends_at: string;
  current_period_end: string;
}

interface FeatureFormState {
  max_users: string;
  email_enabled: boolean;
  email_monthly_limit: string;
  custom_pipeline: boolean;
  automation_enabled: boolean;
  daily_lead_limit: string;
  max_contacts: string;
  max_websites: string;
  enrichment_enabled: boolean;
  prospecting_enabled: boolean;
  api_access_enabled: boolean;
  flagsJson: string;
}

const Tenants: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    action: string;
    tenantId: string;
    tenantName: string;
  } | null>(null);
  const [tenantDetail, setTenantDetail] = useState<TenantDetail | null>(null);
  const [tenantDetailLoading, setTenantDetailLoading] = useState(false);
  const [subscriptionForm, setSubscriptionForm] = useState<SubscriptionFormState | null>(null);
  const [billingLockMode, setBillingLockMode] = useState<'inherit' | 'force_locked' | 'force_unlocked'>('inherit');
  const [featureForm, setFeatureForm] = useState<FeatureFormState | null>(null);
  const [savingSubscription, setSavingSubscription] = useState(false);
  const [savingAccessControls, setSavingAccessControls] = useState(false);
  const [detailMessage, setDetailMessage] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const toLocalDateTime = (iso: string | null | undefined) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const localOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - localOffset).toISOString().slice(0, 16);
  };

  const toIsoOrNull = (value: string) => {
    if (!value.trim()) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toISOString();
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/platform/admin/tenants/');
      setTenants(data);
    } catch {
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleAction = async () => {
    if (!confirmModal) return;
    const { action, tenantId } = confirmModal;
    setActionLoading(tenantId);
    try {
      if (action === 'suspend') {
        await apiClient.post(`/platform/admin/tenants/${tenantId}/suspend/`, {});
      } else if (action === 'reactivate') {
        await apiClient.post(`/platform/admin/tenants/${tenantId}/reactivate/`, {});
      } else if (action === 'soft-delete') {
        await apiClient.post(`/platform/admin/tenants/${tenantId}/soft-delete/`, {});
      }
      setConfirmModal(null);
      await fetchTenants();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSoftDelete = async (tenantId: string) => {
    setActionLoading(tenantId);
    try {
      await apiClient.post(`/platform/admin/tenants/${tenantId}/soft-delete/`, {});
      setConfirmModal(null);
      await fetchTenants();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (tenantId: string) => {
    try {
      await apiClient.post(`/platform/admin/impersonate/${tenantId}/`, {});
      navigate('/');
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to start impersonation');
    }
  };

  const openTenantDetails = async (tenantId: string) => {
    setTenantDetailLoading(true);
    setDetailError(null);
    setDetailMessage(null);
    try {
      const detail = await apiClient.get(`/platform/admin/tenants/${tenantId}/`);
      const typed = detail as TenantDetail;
      setTenantDetail(typed);
      setBillingLockMode(typed.access_controls?.billing_lock_mode || 'inherit');
      if (typed.subscription) {
        setSubscriptionForm({
          status: typed.subscription.status,
          tier: typed.subscription.tier,
          trial_ends_at: toLocalDateTime(typed.subscription.trial_ends_at),
          current_period_end: toLocalDateTime(typed.subscription.current_period_end),
        });
      } else {
        setSubscriptionForm(null);
      }
      const overrides = (typed.access_controls?.feature_overrides || {}) as Record<string, unknown>;
      const flags = (overrides.flags && typeof overrides.flags === 'object' ? overrides.flags : {}) as Record<string, boolean>;
      setFeatureForm({
        max_users: typeof overrides.max_users === 'number' ? String(overrides.max_users) : '',
        email_enabled: typeof overrides.email_enabled === 'boolean' ? overrides.email_enabled : typed.entitlements.email_enabled,
        email_monthly_limit: typeof overrides.email_monthly_limit === 'number' ? String(overrides.email_monthly_limit) : '',
        custom_pipeline: typeof overrides.custom_pipeline === 'boolean' ? overrides.custom_pipeline : typed.entitlements.custom_pipeline,
        automation_enabled: typeof overrides.automation_enabled === 'boolean' ? overrides.automation_enabled : typed.entitlements.automation_enabled,
        daily_lead_limit: typeof overrides.daily_lead_limit === 'number' ? String(overrides.daily_lead_limit) : '',
        max_contacts: typeof overrides.max_contacts === 'number' ? String(overrides.max_contacts) : '',
        max_websites: typeof overrides.max_websites === 'number' ? String(overrides.max_websites) : '',
        enrichment_enabled: typeof overrides.enrichment_enabled === 'boolean' ? overrides.enrichment_enabled : Boolean(typed.entitlements.enrichment_enabled),
        prospecting_enabled: typeof overrides.prospecting_enabled === 'boolean' ? overrides.prospecting_enabled : Boolean(typed.entitlements.prospecting_enabled),
        api_access_enabled: typeof overrides.api_access_enabled === 'boolean' ? overrides.api_access_enabled : Boolean(typed.entitlements.api_access_enabled),
        flagsJson: JSON.stringify(flags, null, 2),
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to load tenant details');
    } finally {
      setTenantDetailLoading(false);
    }
  };

  const refreshTenantDetail = async (tenantId: string) => {
    const detail = await apiClient.get(`/platform/admin/tenants/${tenantId}/`);
    const typed = detail as TenantDetail;
    setTenantDetail(typed);
    setBillingLockMode(typed.access_controls?.billing_lock_mode || 'inherit');
    if (typed.subscription) {
      setSubscriptionForm({
        status: typed.subscription.status,
        tier: typed.subscription.tier,
        trial_ends_at: toLocalDateTime(typed.subscription.trial_ends_at),
        current_period_end: toLocalDateTime(typed.subscription.current_period_end),
      });
    } else {
      setSubscriptionForm(null);
    }
    const overrides = (typed.access_controls?.feature_overrides || {}) as Record<string, unknown>;
    const flags = (overrides.flags && typeof overrides.flags === 'object' ? overrides.flags : {}) as Record<string, boolean>;
    setFeatureForm({
      max_users: typeof overrides.max_users === 'number' ? String(overrides.max_users) : '',
      email_enabled: typeof overrides.email_enabled === 'boolean' ? overrides.email_enabled : typed.entitlements.email_enabled,
      email_monthly_limit: typeof overrides.email_monthly_limit === 'number' ? String(overrides.email_monthly_limit) : '',
      custom_pipeline: typeof overrides.custom_pipeline === 'boolean' ? overrides.custom_pipeline : typed.entitlements.custom_pipeline,
      automation_enabled: typeof overrides.automation_enabled === 'boolean' ? overrides.automation_enabled : typed.entitlements.automation_enabled,
      daily_lead_limit: typeof overrides.daily_lead_limit === 'number' ? String(overrides.daily_lead_limit) : '',
      max_contacts: typeof overrides.max_contacts === 'number' ? String(overrides.max_contacts) : '',
      max_websites: typeof overrides.max_websites === 'number' ? String(overrides.max_websites) : '',
      enrichment_enabled: typeof overrides.enrichment_enabled === 'boolean' ? overrides.enrichment_enabled : Boolean(typed.entitlements.enrichment_enabled),
      prospecting_enabled: typeof overrides.prospecting_enabled === 'boolean' ? overrides.prospecting_enabled : Boolean(typed.entitlements.prospecting_enabled),
      api_access_enabled: typeof overrides.api_access_enabled === 'boolean' ? overrides.api_access_enabled : Boolean(typed.entitlements.api_access_enabled),
      flagsJson: JSON.stringify(flags, null, 2),
    });
  };

  const handleSaveSubscription = async () => {
    if (!tenantDetail || !subscriptionForm) return;
    setSavingSubscription(true);
    setDetailError(null);
    setDetailMessage(null);
    try {
      await apiClient.patch(`/platform/admin/tenants/${tenantDetail.id}/subscription/`, {
        status: subscriptionForm.status,
        tier: subscriptionForm.tier,
        trial_ends_at: toIsoOrNull(subscriptionForm.trial_ends_at),
        current_period_end: toIsoOrNull(subscriptionForm.current_period_end),
      });
      await refreshTenantDetail(tenantDetail.id);
      setDetailMessage('Subscription and plan updated.');
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : 'Failed to update subscription');
    } finally {
      setSavingSubscription(false);
    }
  };

  const handleSaveAccessControls = async () => {
    if (!tenantDetail || !featureForm) return;
    setSavingAccessControls(true);
    setDetailError(null);
    setDetailMessage(null);
    try {
      let parsedFlags: Record<string, boolean> = {};
      try {
        const parsed = featureForm.flagsJson.trim() ? JSON.parse(featureForm.flagsJson) : {};
        if (parsed && typeof parsed === 'object') {
          const entries = Object.entries(parsed).filter((entry) => typeof entry[0] === 'string' && typeof entry[1] === 'boolean');
          parsedFlags = entries.reduce<Record<string, boolean>>((acc, [k, v]) => {
            acc[k] = v as boolean;
            return acc;
          }, {});
        }
      } catch {
        throw new Error('Flags JSON must be a valid object of boolean values.');
      }

      const feature_overrides: Record<string, unknown> = {
        email_enabled: featureForm.email_enabled,
        custom_pipeline: featureForm.custom_pipeline,
        automation_enabled: featureForm.automation_enabled,
        enrichment_enabled: featureForm.enrichment_enabled,
        prospecting_enabled: featureForm.prospecting_enabled,
        api_access_enabled: featureForm.api_access_enabled,
        flags: parsedFlags,
      };

      if (featureForm.max_users.trim()) feature_overrides.max_users = Number(featureForm.max_users);
      if (featureForm.email_monthly_limit.trim()) feature_overrides.email_monthly_limit = Number(featureForm.email_monthly_limit);
      if (featureForm.daily_lead_limit.trim()) feature_overrides.daily_lead_limit = Number(featureForm.daily_lead_limit);
      if (featureForm.max_contacts.trim()) feature_overrides.max_contacts = Number(featureForm.max_contacts);
      if (featureForm.max_websites.trim()) feature_overrides.max_websites = Number(featureForm.max_websites);

      if (billingLockMode === 'force_locked') {
        const confirmed = window.confirm('Force lock this tenant? This blocks business endpoints until unlocked.');
        if (!confirmed) {
          setSavingAccessControls(false);
          return;
        }
      }

      await apiClient.patch(`/platform/admin/tenants/${tenantDetail.id}/access-controls/`, {
        billing_lock_mode: billingLockMode,
        feature_overrides,
      });
      await refreshTenantDetail(tenantDetail.id);
      setDetailMessage('Access controls updated.');
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : 'Failed to update access controls');
    } finally {
      setSavingAccessControls(false);
    }
  };

  const formatRevenue = (v: string) => {
    const n = parseFloat(v);
    if (isNaN(n) || n === 0) return '$0';
    return `$${n.toFixed(2)}`;
  };

  const columns = useMemo<Column<TenantRow>[]>(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        mobilePriority: 'primary',
        mobileLabel: 'Tenant',
        render: (t) => <span className="text-sm text-white">{t.name}</span>,
      },
      {
        id: 'plan',
        header: 'Plan',
        mobileLabel: 'Plan',
        render: (t) => <span className="text-sm text-fg-muted">{t.plan}</span>,
      },
      {
        id: 'domain',
        header: 'Domain',
        mobileLabel: 'Domain',
        render: (t) => <span className="text-sm text-fg-muted">{t.website_domain || '-'}</span>,
      },
      {
        id: 'leads',
        header: 'Leads',
        mobileLabel: 'Leads',
        render: (t) => <span className="text-sm text-fg-muted">{t.total_leads}</span>,
      },
      {
        id: 'revenue',
        header: 'Revenue (30d)',
        mobileLabel: 'Revenue (30d)',
        render: (t) => <span className="text-sm text-fg-muted">{formatRevenue(t.revenue_30d)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (t) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
              t.status === 'active' ? 'bg-success-bg text-success-fg' : 'bg-amber-500/20 text-amber-400'
            }`}
          >
            {t.status}
          </span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        mobileLabel: 'Created',
        render: (t) => (
          <span className="text-sm text-fg-muted">
            {t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (t) => (
          <div data-row-action className="flex flex-wrap gap-2">
            <button
              onClick={() => openTenantDetails(t.id)}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-surface-muted hover:opacity-90 text-white disabled:opacity-50"
            >
              View Details
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Impersonate tenant "${t.name}"? You will be redirected to their dashboard.`)) {
                  handleImpersonate(t.id);
                }
              }}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-amber-600/80 hover:bg-amber-600 text-white disabled:opacity-50"
            >
              Impersonate
            </button>
            <button
              onClick={() => {
                setConfirmModal({
                  action: t.status === 'active' ? 'suspend' : 'reactivate',
                  tenantId: t.id,
                  tenantName: t.name,
                });
              }}
              disabled={actionLoading !== null}
              className="text-xs px-2 py-1 rounded bg-surface-muted hover:opacity-90 text-white disabled:opacity-50"
            >
              {t.status === 'active' ? 'Suspend' : 'Reactivate'}
            </button>
            {t.name !== 'System' && (
              <button
                onClick={() => {
                  if (window.confirm(`Soft delete tenant "${t.name}"? This can be restored.`)) {
                    handleSoftDelete(t.id);
                  }
                }}
                disabled={actionLoading !== null}
                className="text-xs px-2 py-1 rounded bg-red-900/50 hover:bg-red-900/70 text-red-300 disabled:opacity-50"
              >
                Soft Delete
              </button>
            )}
          </div>
        ),
      },
    ],
    [actionLoading]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Tenant Management</h1>
        <button
          onClick={() => fetchTenants()}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-fg-secondary bg-surface-muted border border-border rounded-lg hover:bg-surface-muted flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowPathIcon size={4} className={loading ? 'animate-spin' : undefined} />
          Refresh
        </button>
      </div>
      <DataListTable<TenantRow>
        columns={columns}
        rows={tenants}
        getRowKey={(t) => t.id}
        loading={loading}
        emptyContent={<div className="text-fg-muted p-4">No tenants found.</div>}
      />

      {confirmModal && confirmModal.action !== 'soft-delete' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setConfirmModal(null)}>
          <div
            className="bg-app border border-border rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-2">
              {confirmModal.action === 'suspend' ? 'Suspend Tenant' : 'Reactivate Tenant'}
            </h3>
            <p className="text-fg-muted mb-4">
              {confirmModal.action === 'suspend'
                ? `Suspend "${confirmModal.tenantName}"? Users will not be able to access.`
                : `Reactivate "${confirmModal.tenantName}"?`}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleAction}
                disabled={actionLoading !== null}
                className="px-4 py-2 rounded-lg bg-amber-500 text-fg font-medium hover:bg-amber-600 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 rounded-lg bg-surface-muted text-white hover:opacity-90"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {(tenantDetail || tenantDetailLoading) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setTenantDetail(null)}
        >
          <div
            className="bg-app border border-border rounded-lg p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Tenant Details</h3>
                <p className="text-sm text-fg-muted">
                  {tenantDetail?.name || 'Loading tenant…'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTenantDetail(null)}
                className="text-sm px-3 py-1 rounded bg-surface-muted text-white hover:opacity-90"
              >
                Close
              </button>
            </div>

            {tenantDetailLoading || !tenantDetail ? (
              <p className="text-fg-muted text-sm">Loading details…</p>
            ) : (
              <div className="space-y-5">
                {detailError && (
                  <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                    {detailError}
                  </div>
                )}
                {detailMessage && (
                  <div className="rounded border border-success-fg/40 bg-success-bg px-3 py-2 text-sm text-success-fg">
                    {detailMessage}
                  </div>
                )}
                <section className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded border border-border p-3">
                    <p className="text-fg-muted text-xs uppercase">Tenant</p>
                    <p className="text-white font-medium">{tenantDetail.name}</p>
                    <p className="text-fg-muted">{tenantDetail.domain || 'No domain set'}</p>
                  </div>
                  <div className="rounded border border-border p-3">
                    <p className="text-fg-muted text-xs uppercase">Status</p>
                    <p className="text-white font-medium">
                      {tenantDetail.is_active ? 'Active' : 'Suspended'}
                    </p>
                    <p className="text-fg-muted">
                      Created {tenantDetail.created_at ? new Date(tenantDetail.created_at).toLocaleDateString() : '—'}
                    </p>
                  </div>
                </section>

                <section className="rounded border border-border p-3 text-sm">
                  <p className="text-fg-muted text-xs uppercase mb-2">Subscription Billing Controls</p>
                  {tenantDetail.subscription ? (
                    <div className="space-y-3">
                      <div className="grid sm:grid-cols-2 gap-2 text-fg-muted">
                        <p>Plan: <span className="text-white">{tenantDetail.subscription.plan}</span></p>
                        <p>Tier: <span className="text-white">{tenantDetail.subscription.tier}</span></p>
                        <p>Billing: <span className="text-white">{tenantDetail.subscription.billing_period || '—'}</span></p>
                        <p>Price: <span className="text-white">${tenantDetail.subscription.price}</span></p>
                      </div>
                      {subscriptionForm && (
                        <div className="grid sm:grid-cols-2 gap-3">
                          <label className="text-xs text-fg-muted">
                            Plan (tier)
                            <select
                              value={subscriptionForm.tier}
                              onChange={(e) => {
                                const tier = e.target.value;
                                // Lite is included with a Mamba website: active, with no trial or billing period.
                                setSubscriptionForm(
                                  tier === 'lite'
                                    ? { ...subscriptionForm, tier, status: 'active', trial_ends_at: '', current_period_end: '' }
                                    : { ...subscriptionForm, tier },
                                );
                              }}
                              className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                            >
                              <option value="basic">BASIC</option>
                              <option value="lite">LITE (included with Mamba website)</option>
                              <option value="pro">PRO</option>
                              <option value="growth">GROWTH</option>
                            </select>
                            <span className="mt-1 block text-[10px] text-fg-muted">
                              Drives entitlements immediately. Manual override — does not change Stripe billing.
                            </span>
                          </label>
                          <label className="text-xs text-fg-muted">
                            Status
                            <select
                              value={subscriptionForm.status}
                              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, status: e.target.value })}
                              className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                            >
                              <option value="trial">trial</option>
                              <option value="active">active</option>
                              <option value="past_due">past_due</option>
                              <option value="cancelled">cancelled</option>
                              <option value="expired">expired</option>
                            </select>
                          </label>
                          <label className="text-xs text-fg-muted">
                            Trial ends
                            <input
                              type="datetime-local"
                              value={subscriptionForm.trial_ends_at}
                              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, trial_ends_at: e.target.value })}
                              className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                            />
                          </label>
                          <label className="text-xs text-fg-muted">
                            Current period end
                            <input
                              type="datetime-local"
                              value={subscriptionForm.current_period_end}
                              onChange={(e) => setSubscriptionForm({ ...subscriptionForm, current_period_end: e.target.value })}
                              className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                            />
                          </label>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveSubscription}
                        disabled={savingSubscription || !subscriptionForm}
                        className="rounded bg-primary px-3 py-2 text-xs font-medium text-primary-fg hover:bg-primary-dark disabled:opacity-50"
                      >
                        {savingSubscription ? 'Saving...' : 'Save Subscription Controls'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-fg-muted">No subscription record found.</p>
                  )}
                </section>

                <section className="rounded border border-border p-3 text-sm">
                  <p className="text-fg-muted text-xs uppercase mb-2">Usage Metrics</p>
                  <div className="grid sm:grid-cols-2 gap-2 text-fg-muted">
                    <p>Total users: <span className="text-white">{tenantDetail.metrics.total_users}</span></p>
                    <p>Active users: <span className="text-white">{tenantDetail.metrics.active_users}</span></p>
                    <p>Total websites: <span className="text-white">{tenantDetail.metrics.total_websites}</span></p>
                    <p>Active websites: <span className="text-white">{tenantDetail.metrics.active_websites}</span></p>
                    <p>Total leads: <span className="text-white">{tenantDetail.metrics.total_leads}</span></p>
                    <p>Leads (30d): <span className="text-white">{tenantDetail.metrics.leads_30d}</span></p>
                    <p>Revenue (30d): <span className="text-white">{formatRevenue(tenantDetail.metrics.revenue_30d)}</span></p>
                    <p>Latest event: <span className="text-white">{tenantDetail.metrics.latest_event_at ? new Date(tenantDetail.metrics.latest_event_at).toLocaleString() : '—'}</span></p>
                  </div>
                </section>

                <section className="rounded border border-border p-3 text-sm">
                  <p className="text-fg-muted text-xs uppercase mb-2">Entitlements</p>
                  <div className="grid sm:grid-cols-2 gap-2 text-fg-muted">
                    <p>Tier: <span className="text-white">{tenantDetail.entitlements.tier_display}</span></p>
                    <p>Plan code: <span className="text-white">{tenantDetail.entitlements.plan_code || '—'}</span></p>
                    <p>Max users: <span className="text-white">{tenantDetail.entitlements.max_users}</span></p>
                    <p>Custom pipeline: <span className="text-white">{tenantDetail.entitlements.custom_pipeline ? 'Enabled' : 'Disabled'}</span></p>
                    <p>Email enabled: <span className="text-white">{tenantDetail.entitlements.email_enabled ? 'Yes' : 'No'}</span></p>
                    <p>Email monthly limit: <span className="text-white">{tenantDetail.entitlements.email_monthly_limit}</span></p>
                    <p>Automation enabled: <span className="text-white">{tenantDetail.entitlements.automation_enabled ? 'Yes' : 'No'}</span></p>
                    <p>Daily lead limit: <span className="text-white">{tenantDetail.entitlements.daily_lead_limit ?? '—'}</span></p>
                    <p>Portalr included: <span className="text-white">{tenantDetail.entitlements.includes_portalr ? 'Yes' : 'No'}</span></p>
                  </div>
                </section>

                <section className="rounded border border-border p-3 text-sm">
                  <p className="text-fg-muted text-xs uppercase mb-2">Manual Access Controls</p>
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-2 text-fg-muted">
                      <p>
                        Billing lock (subscription):{' '}
                        <span className="text-white">
                          {tenantDetail.access_controls?.billing_locked_by_subscription ? 'Locked' : 'Unlocked'}
                        </span>
                      </p>
                      <p>
                        Billing lock (effective):{' '}
                        <span className="text-white">
                          {tenantDetail.access_controls?.billing_locked_effective ? 'Locked' : 'Unlocked'}
                        </span>
                      </p>
                    </div>
                    <label className="text-xs text-fg-muted block">
                      Billing lock mode
                      <select
                        value={billingLockMode}
                        onChange={(e) => setBillingLockMode(e.target.value as 'inherit' | 'force_locked' | 'force_unlocked')}
                        className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                      >
                        <option value="inherit">inherit (use subscription)</option>
                        <option value="force_locked">force_locked</option>
                        <option value="force_unlocked">force_unlocked</option>
                      </select>
                    </label>
                  </div>
                </section>

                <section className="rounded border border-border p-3 text-sm">
                  <p className="text-fg-muted text-xs uppercase mb-2">Allowed Features Overrides</p>
                  {featureForm ? (
                    <div className="space-y-3">
                      <div className="grid sm:grid-cols-3 gap-3">
                        <label className="text-xs text-fg-muted">
                          Max users
                          {tenantDetail.entitlements.max_users_ceiling != null
                            ? ` (up to ${tenantDetail.entitlements.max_users_ceiling})`
                            : ''}
                          <input
                            type="number"
                            min={0}
                            max={tenantDetail.entitlements.max_users_ceiling ?? undefined}
                            value={featureForm.max_users}
                            onChange={(e) => setFeatureForm({ ...featureForm, max_users: e.target.value })}
                            className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                          />
                        </label>
                        <label className="text-xs text-fg-muted">
                          Email monthly limit
                          <input
                            type="number"
                            min={0}
                            value={featureForm.email_monthly_limit}
                            onChange={(e) => setFeatureForm({ ...featureForm, email_monthly_limit: e.target.value })}
                            className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                          />
                        </label>
                        <label className="text-xs text-fg-muted">
                          Daily lead limit
                          <input
                            type="number"
                            min={0}
                            value={featureForm.daily_lead_limit}
                            onChange={(e) => setFeatureForm({ ...featureForm, daily_lead_limit: e.target.value })}
                            className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                          />
                        </label>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <label className="flex items-center gap-2 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={featureForm.email_enabled}
                            onChange={(e) => setFeatureForm({ ...featureForm, email_enabled: e.target.checked })}
                          />
                          Email enabled
                        </label>
                        <label className="flex items-center gap-2 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={featureForm.custom_pipeline}
                            onChange={(e) => setFeatureForm({ ...featureForm, custom_pipeline: e.target.checked })}
                          />
                          Custom pipeline
                        </label>
                        <label className="flex items-center gap-2 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={featureForm.automation_enabled}
                            onChange={(e) => setFeatureForm({ ...featureForm, automation_enabled: e.target.checked })}
                          />
                          Automation enabled
                        </label>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <label className="text-xs text-fg-muted">
                          Max contacts
                          <input
                            type="number"
                            min={0}
                            placeholder={tenantDetail.entitlements.max_contacts != null ? String(tenantDetail.entitlements.max_contacts) : 'unlimited'}
                            value={featureForm.max_contacts}
                            onChange={(e) => setFeatureForm({ ...featureForm, max_contacts: e.target.value })}
                            className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                          />
                        </label>
                        <label className="text-xs text-fg-muted">
                          Connected websites
                          <input
                            type="number"
                            min={0}
                            placeholder={tenantDetail.entitlements.max_websites != null ? String(tenantDetail.entitlements.max_websites) : 'unlimited'}
                            value={featureForm.max_websites}
                            onChange={(e) => setFeatureForm({ ...featureForm, max_websites: e.target.value })}
                            className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-sm text-fg"
                          />
                        </label>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <label className="flex items-center gap-2 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={featureForm.enrichment_enabled}
                            onChange={(e) => setFeatureForm({ ...featureForm, enrichment_enabled: e.target.checked })}
                          />
                          Enrichment (Apollo)
                        </label>
                        <label className="flex items-center gap-2 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={featureForm.prospecting_enabled}
                            onChange={(e) => setFeatureForm({ ...featureForm, prospecting_enabled: e.target.checked })}
                          />
                          Prospecting / lead discovery
                        </label>
                        <label className="flex items-center gap-2 text-xs text-fg-muted">
                          <input
                            type="checkbox"
                            checked={featureForm.api_access_enabled}
                            onChange={(e) => setFeatureForm({ ...featureForm, api_access_enabled: e.target.checked })}
                          />
                          API access (secret keys)
                        </label>
                      </div>
                      <label className="text-xs text-fg-muted block">
                        Extended feature flags JSON (key: boolean)
                        <textarea
                          value={featureForm.flagsJson}
                          onChange={(e) => setFeatureForm({ ...featureForm, flagsJson: e.target.value })}
                          rows={5}
                          className="mt-1 w-full rounded border border-border bg-input px-2 py-2 text-xs text-fg font-mono"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={handleSaveAccessControls}
                        disabled={savingAccessControls}
                        className="rounded bg-primary px-3 py-2 text-xs font-medium text-primary-fg hover:bg-primary-dark disabled:opacity-50"
                      >
                        {savingAccessControls ? 'Saving...' : 'Save Access & Feature Controls'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-fg-muted">Feature overrides are unavailable for this tenant.</p>
                  )}
                </section>

                <section className="rounded border border-border p-3 text-sm">
                  <p className="text-fg-muted text-xs uppercase mb-2">Websites</p>
                  {tenantDetail.websites.length === 0 ? (
                    <p className="text-fg-muted">No websites linked to this tenant.</p>
                  ) : (
                    <ul className="space-y-2">
                      {tenantDetail.websites.map((website) => (
                        <li key={website.id} className="rounded border border-border px-3 py-2 text-fg-muted">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-white">{website.domain}</span>
                            <span className={website.is_active ? 'text-success-fg' : 'text-amber-400'}>
                              {website.is_active ? 'active' : 'disabled'}
                            </span>
                          </div>
                          <p className="text-xs">
                            Last event: {website.last_event_at ? new Date(website.last_event_at).toLocaleString() : '—'}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenants;
